import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NovelCard, NovelCardSkeleton } from '@/components/NovelCard';
import { Button } from '@/components/ui/button';
import { TrendingUp, Clock, Sparkles } from 'lucide-react';
import type { Novel } from '@/types';
import { HeroBanner } from '@/components/HeroBanner';

interface NoticeItem {
  id: string;
  title: string;
  content: string;
  type: 'notice' | 'event' | 'maintenance';
  isPinned?: boolean;
  imageUrl?: string;
  linkUrl?: string;
}

export function HomePage() {
  const [novels, setNovels] = useState<Novel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [aspirantNovels, setAspirantNovels] = useState<Novel[]>([]);
  const [hero, setHero] = useState<NoticeItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/novels?limit=4&sort=latest&tier=aspirant')
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        if (j.success) setAspirantNovels(j.data?.novels || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/notices?limit=1')
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        const first = j?.data?.notices?.[0];
        if (first) setHero(first);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetch('/api/novels?limit=10&sort=views&tier=official')
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setNovels((j.data?.novels || []) as Novel[]);
        }
      })
      .catch((err) => {
        console.error('작품 목록 로드 실패:', err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="container py-6 space-y-8">
      {/* 히어로 섹션 — 활성 공지 1개 또는 기본 환영 메시지 */}
      <HeroBanner notice={hero} />


      {/* 메인 콘텐츠 — 웹소설 위주 풀폭 그리드 */}
      <div className="space-y-8">
        {/* 인기 작품 */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              인기 작품
            </h2>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/novels">전체 보기 →</Link>
            </Button>
          </div>
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"><NovelCardSkeleton count={8} /></div>
          ) : novels.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground border rounded-lg">
              아직 등록된 정식 작품이 없습니다.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {novels.map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
          )}
        </section>

        {/* 신규 작품 */}
        {novels.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                신규 작품
              </h2>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/novels">전체 보기 →</Link>
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...novels].reverse().map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
          </section>
        )}

        {/* 지망생 신인 — 데이터 0건이면 섹션 자체 숨김 */}
        {aspirantNovels.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                지망생 신인
              </h2>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/aspirant">전체 보기 →</Link>
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {aspirantNovels.map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
